import { type Tone } from './Pill'
import { isSelectable, type SelectOption } from './Select'

/**
 * What an option-set VALUE means, resolved once for every surface that shows one.
 *
 * ── The defect this exists to stop ──────────────────────────────────────────
 * The console had two generic renderers for the same `core.field_meta` and they
 * disagreed. `EntityForm` routed `kind === 'option'` through the shared
 * `Select`; the record page's inline editor special-cased only `date` and sent
 * every other kind — including `option` — into a raw `<input type="text">`. So
 * one field displayed its registered label in read mode and became unrestricted
 * free text the moment you clicked it. Measured live on MBN on the Q8
 * Organization `Kind` field (#1466).
 *
 * The same page printed the field's `kind` as helper copy, so the implementation
 * word `option` appeared under the label — storage vocabulary presented to a
 * customer as if it were guidance.
 *
 * ── Decoration is a CLOSED vocabulary, and that is the point ────────────────
 * A registered option may carry a `tone` and an `icon`. Both are governed keys,
 * never CSS: `tone` must be one of `Pill`'s five, and `icon` is resolved by
 * `EntityIcon`'s registry. Anything else falls back to neutral rather than
 * rendering, because a token that does not resolve paints nothing and does it
 * silently — `material.test.ts` records that exact cost, where a badge drew
 * white text on no background for as long as it had shipped.
 *
 * So an unknown tone is NOT an error and NOT a colour: it is neutral, and the
 * option still reads correctly. Decoration may only ever add.
 *
 * ── An unrecognised VALUE is a different thing entirely ────────────────────
 * A value the registered set does not contain has no label. Printing the raw
 * key is what row 3 forbids; hiding it would be worse, because the record
 * genuinely holds it and somebody has to be able to see that. So it resolves to
 * `known: false` and the renderer says so in words — the storage key is
 * evidence for an operator, not the normal presentation of a field.
 *
 * That state should be impossible once the write side refuses unregistered
 * values (#1466 row 7, held). It is reachable today, so it is rendered honestly
 * rather than assumed away.
 */

/** `Pill`'s tones, which are the only ones a stylesheet actually defines. */
const TONES: readonly Tone[] = ['good', 'warn', 'critical', 'neutral', 'brand']

/** An option as a catalogue hands it over, plus its optional decoration. */
export interface DecoratedOption extends SelectOption {
  /** A governed tone key. Anything outside `Pill`'s five reads as neutral. */
  tone?: string | null
  /** An `EntityIcon` registry key. An unknown key falls back to its own glyph. */
  icon?: string | null
}

export interface ResolvedOption {
  /** Is this value in the registered set at all? */
  known: boolean
  /** The registered label. For an unknown value, the raw stored key. */
  label: string
  tone: Tone
  icon: string | null
  /** Present, but closed to a NEW choice (`selectable: false`). */
  archived: boolean
  /** The stored value, unchanged. */
  value: string
}

/** Closed set in, closed set out. An unknown tone is neutral, never a colour. */
export function normalizeTone(raw: unknown): Tone {
  return TONES.includes(raw as Tone) ? (raw as Tone) : 'neutral'
}

/**
 * Resolve one stored value against the registered options.
 *
 * `null`/`undefined`/`''` is EMPTY, not unknown — a field nobody has filled in
 * is a normal state and must not be reported as data corruption.
 */
export function resolveOption(
  value: unknown,
  options: readonly DecoratedOption[] | null | undefined,
): ResolvedOption | null {
  if (value === null || value === undefined || value === '') return null
  const stored = String(value)
  const match = (options ?? []).find((option) => option.value === stored)
  if (!match) {
    return { known: false, label: stored, tone: 'critical', icon: null, archived: false, value: stored }
  }
  return {
    known: true,
    label: match.label || stored,
    tone: normalizeTone(match.tone),
    icon: match.icon?.trim() || null,
    archived: !isSelectable(match),
    value: stored,
  }
}

/**
 * The registered label of a stored value, as PLAIN TEXT.
 *
 * For a surface that has no room for a chip — a read-only `<Input>`, a cell, a
 * title attribute. Empty stays empty; an unrecognised value is named in words
 * rather than printed bare, because in a text control a raw key is
 * indistinguishable from a label, which is the defect row 3 is about.
 */
export function optionLabel(
  value: unknown, options: readonly DecoratedOption[] | null | undefined,
): string {
  const resolved = resolveOption(value, options)
  if (!resolved) return ''
  return resolved.known ? resolved.label : `${resolved.value} — not a registered option`
}

/**
 * ── WHAT USED TO BE HERE, AND WHY IT IS NOT ─────────────────────────────────
 * A `SEARCH_THRESHOLD` and a `filterOptions()` — an accent-folded contains
 * match over label and stored key — for an in-menu filter on long option sets.
 * Both are deleted, for two separate reasons, and neither is "we changed our
 * minds":
 *
 *   · the FILTER cannot exist inside a Radix listbox at all (`Select.tsx` holds
 *     the two measurements), so nothing consumed them;
 *   · `filterOptions` was a SECOND COPY. `packages/ui/src/textSearch.ts`
 *     already exports `matchesText(query, ...values)`, which normalises NFKD,
 *     strips combining marks, lowercases and requires every token to occur
 *     somewhere — the same predicate, already shared, already tested. Keeping
 *     a private one beside it is the exact defect this lane exists to remove.
 *
 * A future combobox filters with `matchesText`.
 */
